import { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getImports, uploadImportFile, processImport, previewImport, getImportErrors } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Upload, FileSpreadsheet, Play, Loader2, CheckCircle, XCircle, AlertCircle, Eye, Info, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ImportCenterPage() {
  const { t, language } = useLanguage();
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [importType, setImportType] = useState('projects');
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null); // { importId, data }

  const fetchImports = async () => {
    try {
      const response = await getImports();
      setImports(response.data);
    } catch (error) {
      console.error('Failed to fetch imports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchImports(); }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files[0]) await handleFileUpload(files[0]);
  }, [importType]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handleFileUpload(file);
  };

  const handleFileUpload = async (file) => {
    const validExtensions = ['.xlsx', '.xls', '.json'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(ext)) { toast.error(t('supported_formats')); return; }

    setUploading(true);
    try {
      const response = await uploadImportFile(file, importType);
      toast.success(t('file_uploaded'));
      fetchImports();
      // Auto-preview
      handlePreview(response.data.import_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (importId) => {
    setPreviewing(importId);
    try {
      const res = await previewImport(importId);
      setPreview({ importId, ...res.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل فحص الملف');
    } finally {
      setPreviewing(null);
    }
  };

  const handleProcess = async (importId) => {
    setProcessing(importId);
    try {
      const response = await processImport(importId);
      toast.success(`تم الاستيراد: ${response.data.success_rows} / ${response.data.total_rows}`);
      setPreview(null);
      fetchImports();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      completed_with_errors: 'bg-orange-100 text-orange-800',
      failed: 'bg-red-100 text-red-800',
    };
    return <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.pending}`}>{t(status)}</span>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'completed_with_errors': return <AlertCircle className="w-4 h-4 text-orange-600" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="import-center-page">
      {/* Upload Section */}
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            {t('upload_file')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Info banner */}
            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="import-info-banner">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-bold mb-1">استيراد دفعي أولي</p>
                <p>الملف يحتوي على البيانات الأساسية فقط (اسم المشروع، المنطقة، المدينة، الإحداثيات...). البيانات التفصيلية (صور، منتجات، قنوات بيع، ملف PDF) يتم استكمالها لاحقاً من شاشة تعديل المشروع.</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">{t('import_type')}:</span>
              <Select value={importType} onValueChange={setImportType}>
                <SelectTrigger className="w-48" data-testid="import-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="projects">{t('projects')}</SelectItem>
                  <SelectItem value="outlets">{t('outlets')}</SelectItem>
                  <SelectItem value="categories">{t('categories')}</SelectItem>
                  <SelectItem value="geography">{t('nav.master_data')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dropzone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive ? 'border-[#0A4C3A] bg-[#0A4C3A]/5' : 'border-gray-300 hover:border-[#0A4C3A]'}`}
              onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
              data-testid="import-dropzone"
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 animate-spin text-[#0A4C3A]" />
                  <p className="text-sm text-gray-600">{t('loading')}</p>
                </div>
              ) : (
                <>
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-700 mb-2">{t('drop_file_here')}</p>
                  <p className="text-sm text-gray-500 mb-4">{t('supported_formats')}</p>
                  <input type="file" accept=".xlsx,.xls,.json" onChange={handleFileSelect} className="hidden" id="file-upload" data-testid="import-file-input" />
                  <label htmlFor="file-upload">
                    <Button variant="outline" className="cursor-pointer" asChild><span>{t('select')}</span></Button>
                  </label>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview Panel */}
      {preview && (
        <Card className="bg-white border-2 border-[#0A4C3A]/20" data-testid="import-preview-panel">
          <CardHeader className="bg-[#0A4C3A]/5">
            <CardTitle className="flex items-center gap-2 text-[#0A4C3A]">
              <Eye className="w-5 h-5" />
              فحص الملف قبل الاستيراد
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg text-center" data-testid="preview-total">
                <span className="block text-2xl font-bold text-gray-800">{preview.total_rows}</span>
                <span className="text-xs text-gray-500">إجمالي السجلات</span>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center" data-testid="preview-ready">
                <span className="block text-2xl font-bold text-green-700">{preview.ready_rows}</span>
                <span className="text-xs text-green-600">جاهز للاستيراد</span>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg text-center" data-testid="preview-missing">
                <span className="block text-2xl font-bold text-yellow-700">{preview.missing_optional_total}</span>
                <span className="text-xs text-yellow-600">حقول اختيارية ناقصة</span>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center" data-testid="preview-errors">
                <span className="block text-2xl font-bold text-red-700">{preview.error_rows}</span>
                <span className="text-xs text-red-600">أخطاء حقيقية</span>
              </div>
            </div>

            {/* Note */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">البيانات غير الموجودة في الملف (صور، منتجات، قنوات بيع، ملف PDF...) سيتم استكمالها لاحقاً من شاشة تعديل المشروع في الأدمن.</p>
            </div>

            {/* Columns found */}
            {preview.columns_found?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">الأعمدة الموجودة في الملف:</p>
                <div className="flex flex-wrap gap-1.5">
                  {preview.columns_found.map((c, i) => (
                    <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full border">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Errors list */}
            {preview.errors?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-red-700 mb-2">أخطاء ({preview.error_rows}):</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {preview.errors.map((e, i) => (
                    <div key={i} className="text-xs p-2 bg-red-50 rounded border border-red-100 text-red-700">
                      صف {e.row}: {e.message}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                className="bg-[#0A4C3A] hover:bg-[#073B2D] flex-1"
                onClick={() => handleProcess(preview.importId)}
                disabled={processing === preview.importId || preview.ready_rows === 0}
                data-testid="preview-process-btn"
              >
                {processing === preview.importId ? (
                  <Loader2 className="w-4 h-4 animate-spin me-2" />
                ) : (
                  <Play className="w-4 h-4 me-2" />
                )}
                استيراد {preview.ready_rows} مشروع
              </Button>
              <Button variant="outline" onClick={() => setPreview(null)} data-testid="preview-cancel-btn">
                <ArrowLeft className="w-4 h-4 me-1" /> إلغاء
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import History */}
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <CardTitle>{t('import_history')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">{t('file_name')}</TableHead>
                <TableHead className="font-medium">{t('import_type')}</TableHead>
                <TableHead className="font-medium">{t('import_status')}</TableHead>
                <TableHead className="font-medium">{t('total_rows')}</TableHead>
                <TableHead className="font-medium">{t('success_rows')}</TableHead>
                <TableHead className="font-medium">{t('error_rows')}</TableHead>
                <TableHead className="font-medium">{t('created_at')}</TableHead>
                <TableHead className="font-medium text-center">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0A4C3A]" /></TableCell></TableRow>
              ) : imports.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">{t('no_data')}</TableCell></TableRow>
              ) : (
                imports.map((imp) => (
                  <TableRow key={imp.id} data-testid={`import-row-${imp.id}`}>
                    <TableCell className="font-medium max-w-xs truncate">{imp.filename}</TableCell>
                    <TableCell>{t(imp.import_type)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(imp.status)}
                        {getStatusBadge(imp.status)}
                      </div>
                    </TableCell>
                    <TableCell>{imp.total_rows || 0}</TableCell>
                    <TableCell className="text-green-600">{imp.success_rows || 0}</TableCell>
                    <TableCell className="text-red-600">{imp.error_rows || 0}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {imp.created_at ? new Date(imp.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        {imp.status === 'pending' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handlePreview(imp.id)} disabled={previewing === imp.id} data-testid={`preview-import-${imp.id}`}>
                              {previewing === imp.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Eye className="w-4 h-4 me-1" /> فحص</>}
                            </Button>
                            <Button size="sm" onClick={() => handleProcess(imp.id)} disabled={processing === imp.id} className="bg-[#0A4C3A] hover:bg-[#073B2D]" data-testid={`process-import-${imp.id}`}>
                              {processing === imp.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 me-1" /> {t('process_import')}</>}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
